// Aptitud laboral (Centro Médico → Ocupacional). Registra certificados de aptitud
// (preocupacional/periódico/retiro…) con resultado, restricciones y vigencia.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { fechaLocal } from '@/lib/utils/fecha';
import {
  AutoTextarea,
  Aptitud as Apt, Campo, Convenio, Modal, PacienteRow, RESULTADOS_APTITUD, TIPOS_EXAMEN_OCUP,
  fieldCls, medRpc,
} from './shared';

const RES_CHIP: Record<string, string> = {
  apto: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  apto_restricciones: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  no_apto: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  aplazado: 'bg-muted text-muted-foreground',
};
const resLabel = (v: string) => RESULTADOS_APTITUD.find((r) => r.v === v)?.label ?? v;
const exaLabel = (v: string) => TIPOS_EXAMEN_OCUP.find((t) => t.v === v)?.label ?? v;
const vacio = { paciente_id: '', empleador_id: '', cargo: '', tipo_examen: 'preocupacional', resultado: 'apto', restricciones: '', observaciones: '', fecha: fechaLocal(), vigencia_hasta: '' };

export function Aptitud() {
  const qc = useQueryClient();
  const [soloVigentes, setSoloVigentes] = useState(false);
  const { data: rows = [], isFetching } = useQuery<Apt[]>({
    queryKey: ['med-aptitudes', soloVigentes], queryFn: () => medRpc<Apt[]>('med_aptitudes', { _paciente: null, _solo_vigentes: soloVigentes }),
  });
  const { data: pacientes = [] } = useQuery<PacienteRow[]>({ queryKey: ['med-pacientes', ''], queryFn: () => medRpc<PacienteRow[]>('med_pacientes', { _busqueda: '' }) });
  const { data: convenios = [] } = useQuery<Convenio[]>({ queryKey: ['med-convenios', 'empresa'], queryFn: () => medRpc<Convenio[]>('med_convenios', { _tipo: 'empresa' }) });

  const [form, setForm] = useState<typeof vacio>(vacio);
  const [abierto, setAbierto] = useState(false);
  const set = (patch: Partial<typeof vacio>) => setForm((f) => ({ ...f, ...patch }));

  const guardar = useMutation({
    mutationFn: () => medRpc('med_aptitud_guardar', { _data: form }),
    onSuccess: () => { toast.success('Aptitud registrada'); setAbierto(false); setForm(vacio); void qc.invalidateQueries({ queryKey: ['med-aptitudes'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><ShieldCheck className="w-5 h-5" /> Aptitud laboral</h1>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-sm"><input type="checkbox" checked={soloVigentes} onChange={(e) => setSoloVigentes(e.target.checked)} /> Solo vigentes</label>
          <Button onClick={() => { setForm(vacio); setAbierto(true); }}><Plus className="w-4 h-4 mr-1" /> Nueva aptitud</Button>
        </div>
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Certificados ({rows.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">Paciente</th><th className="py-2 pr-3">Empresa</th><th className="py-2 pr-3">Cargo</th>
              <th className="py-2 pr-3">Examen</th><th className="py-2 pr-3">Resultado</th><th className="py-2 pr-3">Vigencia</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Sin certificados</td></tr>}
              {rows.map((a) => (
                <tr key={a.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 whitespace-nowrap">{a.fecha}</td>
                  <td className="py-2 pr-3">{a.paciente}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{a.empleador ?? '—'}</td>
                  <td className="py-2 pr-3">{a.cargo ?? '—'}</td>
                  <td className="py-2 pr-3 text-xs">{exaLabel(a.tipo_examen)}</td>
                  <td className="py-2 pr-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs ${RES_CHIP[a.resultado] ?? 'bg-muted'}`}>{resLabel(a.resultado)}</span>
                    {a.restricciones && <div className="text-xs text-muted-foreground max-w-[14rem] truncate" title={a.restricciones}>{a.restricciones}</div>}
                  </td>
                  <td className={`py-2 pr-3 whitespace-nowrap text-xs ${a.vigente ? '' : 'text-red-600 font-medium'}`}>{a.vigencia_hasta ?? 'Sin vencimiento'}{!a.vigente ? ' (vencida)' : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title="Nueva aptitud laboral" onClose={() => setAbierto(false)} wide>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Paciente *">
                <select className={fieldCls} value={form.paciente_id} onChange={(e) => set({ paciente_id: e.target.value })}>
                  <option value="">— Selecciona —</option>{pacientes.map((p) => <option key={p.id} value={p.id}>{p.razon_social} ({p.identificacion ?? 's/i'})</option>)}
                </select>
              </Campo>
              <Campo label="Empresa empleadora">
                <select className={fieldCls} value={form.empleador_id} onChange={(e) => set({ empleador_id: e.target.value })}>
                  <option value="">— Ninguna —</option>{convenios.map((c) => <option key={c.id} value={c.id}>{c.razon_social}</option>)}
                </select>
              </Campo>
              <Campo label="Cargo"><input className={fieldCls} value={form.cargo} onChange={(e) => set({ cargo: e.target.value })} /></Campo>
              <Campo label="Tipo de examen">
                <select className={fieldCls} value={form.tipo_examen} onChange={(e) => set({ tipo_examen: e.target.value })}>
                  {TIPOS_EXAMEN_OCUP.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
                </select>
              </Campo>
              <Campo label="Resultado">
                <select className={fieldCls} value={form.resultado} onChange={(e) => set({ resultado: e.target.value })}>
                  {RESULTADOS_APTITUD.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
                </select>
              </Campo>
              <Campo label="Fecha"><input type="date" className={fieldCls} value={form.fecha} onChange={(e) => set({ fecha: e.target.value })} /></Campo>
              <Campo label="Vigencia hasta"><input type="date" min={form.fecha} className={fieldCls} value={form.vigencia_hasta} onChange={(e) => set({ vigencia_hasta: e.target.value })} /></Campo>
            </div>
            <Campo label="Restricciones"><AutoTextarea value={form.restricciones} onChange={(e) => set({ restricciones: e.target.value })} /></Campo>
            <Campo label="Observaciones"><AutoTextarea value={form.observaciones} onChange={(e) => set({ observaciones: e.target.value })} /></Campo>
            <div className="flex justify-end gap-2 border-t pt-3">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={!form.paciente_id} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
